import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

export default function VisitorLog() {
  const { user, profile, canEditData } = useAuth()
  const [logs, setLogs] = useState([])
  const [tab, setTab] = useState('new')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  const today = ugandaDate()
  const now = new Date().toTimeString().slice(0,5)

  const emptyRow = () => ({ visitor_name: '', organisation: '', purpose: '', time_in: now, time_out: '', host: profile?.full_name || '' })
  const [form, setForm] = useState({ visit_date: today, visitors: [emptyRow()] })

  useEffect(() => { if (tab === 'history') loadLogs() }, [tab])

  const loadLogs = async () => {
    setLoading(true)
    const { data } = await supabase.from('visitor_logs').select('*, profiles(full_name)').order('visit_date', { ascending: false }).limit(100)
    setLogs(data || [])
    setLoading(false)
  }

  const setRow = (i, field, val) => {
    const visitors = [...form.visitors]
    visitors[i] = { ...visitors[i], [field]: val }
    setForm(f => ({ ...f, visitors }))
  }

  const handleSave = async () => {
    const valid = form.visitors.filter(v => v.visitor_name)
    if (!valid.length) { setError('Please enter at least one visitor name.'); return }
    setSaving(true); setError(''); setSaved(false)
    const inserts = valid.map(v => ({ ...v, visit_date: form.visit_date, user_id: user.id }))
    const { error: err } = await supabase.from('visitor_logs').insert(inserts)
    if (err) { setError(err.message); setSaving(false); return }
    setSaved(true); setSaving(false)
    setTimeout(() => setSaved(false), 3000)
    setForm({ visit_date: today, visitors: [emptyRow()] })
    loadLogs()
  }

  return (
    <AppLayout title="Visitor Log">
      <div className="section-header">
        <div className="section-icon">👤</div>
        <div className="section-info">
          <h2>Visitor Log</h2>
          <p>Every visitor must be logged — tradespeople, inspectors, suppliers, family. Food safety requirement.</p>
        </div>
      </div>
      <div className="alert alert-amber">⚠️ Food safety requirement: ALL visitors to the farm must be logged. No exceptions.</div>

      <div className="tabs">
        <button className={`tab ${tab==='new'?'active':''}`} onClick={() => setTab('new')}>📝 Log Visitor</button>
        <button className={`tab ${tab==='history'?'active':''}`} onClick={() => setTab('history')}>📚 History</button>
      </div>

      {tab === 'new' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Visitor(s) logged successfully!</div>}
          {error && <div className="alert alert-red">❌ {error}</div>}

          <div className="form-group" style={{ marginBottom: 16, maxWidth: 220 }}>
            <label className="form-label">Visit Date</label>
            <input className="form-input" type="date" value={form.visit_date} onChange={e => setForm(f=>({...f,visit_date:e.target.value}))} disabled={!canEditData} />
          </div>

          <div className="table-wrap" style={{ marginBottom: 10 }}>
            <table>
              <thead><tr><th>Visitor Name *</th><th>Organisation</th><th>Purpose of Visit</th><th>Time In</th><th>Time Out</th><th>Host (who let them in)</th><th style={{ width: 32 }}></th></tr></thead>
              <tbody>
                {form.visitors.map((row, i) => (
                  <tr key={i}>
                    <td><input className="form-input" placeholder="Full name" value={row.visitor_name} onChange={e => setRow(i,'visitor_name',e.target.value)} disabled={!canEditData} /></td>
                    <td><input className="form-input" placeholder="Company / Individual" value={row.organisation} onChange={e => setRow(i,'organisation',e.target.value)} disabled={!canEditData} /></td>
                    <td><input className="form-input" placeholder="e.g. Deliver nutrients" value={row.purpose} onChange={e => setRow(i,'purpose',e.target.value)} disabled={!canEditData} /></td>
                    <td><input className="form-input" type="time" style={{ width: 100 }} value={row.time_in} onChange={e => setRow(i,'time_in',e.target.value)} disabled={!canEditData} /></td>
                    <td><input className="form-input" type="time" style={{ width: 100 }} value={row.time_out} onChange={e => setRow(i,'time_out',e.target.value)} disabled={!canEditData} /></td>
                    <td><input className="form-input" placeholder="Sharon / Edward" value={row.host} onChange={e => setRow(i,'host',e.target.value)} disabled={!canEditData} /></td>
                    <td>{form.visitors.length > 1 && canEditData && <button className="btn-del-row" onClick={() => setForm(f=>({...f,visitors:f.visitors.filter((_,j)=>j!==i)}))}>✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canEditData && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setForm(f=>({...f,visitors:[...f.visitors,emptyRow()]}))}>+ Add Another Visitor</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? <><span className="spinner" /> Saving...</> : '💾 Save Log'}</button>
              <button className="btn btn-ghost" onClick={() => window.print()}>🖨️ Print</button>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="card">
          <div className="card-title">Visitor History</div>
          {loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
          : logs.length === 0 ? <div className="empty-state"><div className="empty-icon">👤</div><h3>No visitors logged yet</h3></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Visitor</th><th>Organisation</th><th>Purpose</th><th>Time In</th><th>Time Out</th><th>Host</th></tr></thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td style={{ fontSize: '0.78rem', fontWeight: 600 }}>{new Date(log.visit_date).toLocaleDateString('en-UG')}</td>
                      <td style={{ fontWeight: 600 }}>{log.visitor_name}</td>
                      <td style={{ fontSize: '0.78rem' }}>{log.organisation || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{log.purpose || '—'}</td>
                      <td style={{ fontSize: '0.78rem', fontFamily: "'DM Mono',monospace" }}>{log.time_in || '—'}</td>
                      <td style={{ fontSize: '0.78rem', fontFamily: "'DM Mono',monospace" }}>{log.time_out || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{log.host || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
